import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import SenderVerificationAlert from '../../components/SenderVerificationAlert';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import PipelineForm from '../../components/Pipelines/PipelineForm';
import Spinner from '../../components/Spinner';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';
import useCrossDomainCopyPage from '../../hooks/useCrossDomainCopyPage';

const page = `${Page['pipelines']}`;

const PipelinesNewPage = () => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();

  // Use the cross-domain copy page hook
  const { from, fromDomain, clearFlowElements } = useCrossDomainCopyPage();

  const { accountId, domainId } = useParams();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [pipeline, setPipeline] = useState();

  useEffect(() => {
    const defaultPipeline = {
      name: 'New pipeline',
      events: [],
      eventsAction: 'include',
      type: 'event',
      transform: `
(payload) => {
  return payload;
}`,
      filter: `
(payload) => {
  return payload ? true : false;
}`,
      outputType: 's3',
      outputUrl: '',
      headers: [],
    };

    if (from) {
      // Use fromDomain if provided (cross-domain copy), otherwise use current domainId (same domain copy)
      fetch(`/api/admin/accounts/${accountId}/domains/${fromDomain || domainId}/pipelines/${from}`)
        .then(({ pipeline }) => {
          if (!fromDomain) {
            pipeline.name = `Copy of ${pipeline.name}`;
          }

          pipeline.running = false;
          delete pipeline.startedAt;
          delete pipeline.finishedAt;
          delete pipeline.id;

          setPipeline(pipeline);
        })
        .catch(setLoadError);
    } else {
      setPipeline(defaultPipeline);
    }
  }, [fetch, accountId, domainId, from, location, fromDomain, clearFlowElements]);

  const create = (pipeline) => {
    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pipelines`, {
      method: 'POST',
      body: JSON.stringify(pipeline),
    })
      .then(() => {
        navigate(location.pathname.replace('/pipelines/new', `/pipelines`));
      })
      .catch(setUserError);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (pipeline === undefined) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/pipelines` },
    { title: 'New' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | New`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <SenderVerificationAlert />

      <PipelineForm pipeline={pipeline} savePipeline={create} />

      {userError ? (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      ) : null}
    </>
  );
};

export default PipelinesNewPage;
